import _get from 'lodash/get';
import urlResource from '../constants/UrlResources';
import { getCurrentRouteResourceName, getL3TopicInfo, getPageUrlInfoFromLocation } from '../../lib/location-helper';
import Page from '../../lib/page';

// Actions
import fetchTopicTree, { updateL2TopicTree } from './action-topic-list';
import getPopularQuestions from './action-popular-list';
import { fetchQueryParams } from '../../contactUs/actions/action-query-params';
import { clearSuggestions } from '../../contactUs/actions/action-auto-complete';
import getArticleListByTopic from './actions-article-list';
import fetchArticleContent from './action-article-content';
import { getHelpCenterAlerts, postChatBotEligibility, mmtCheck } from '../../contactUs/actions/index';
import { resetToggleRelated } from '../../contactUs/actions/action-content-toggle';
import { resetBreadcrumb, updateBreadcrumb, updateBreadCrumbFromArticleContent } from './action-breadcrumb';
import fetchMachineArticle from './action-machine-article';
import { displayChatBot } from './action-chat-bot';
import getICFGuides from './action-allICF-guides';
import { fetchGuideContents } from './action-icf-guide';
import { icfClicked } from './action-icf-page';
// eslint-disable-next-line no-unused-vars
import { wasChatBotClicked } from '../../lib/chat-bot-live-helper';
import { isNaaAlertEnabledInRC, isCustomChatBotPromptConfig } from '../../lib/globalHelper';
import { fetchArticleSearchResult } from '../../contactUs/actions/action-article-search';
import { receiveL3ArticlesResponse } from './action-faq-list';
import i18n from '../../lib/reacti18n';
import { isL3TopicClicked, resetTopicDetails } from './action-L3Topic-list';
// experimentation
import { fetchArticleSearchOnlyFaqBot } from '../../../experimentations/actions/action-article-search';
// V2 Actions
import { fetchTopicTree as fetchTopicTreeV2 } from '../../../jsV2/components/topicTree/TopicTree-Actions';
import { getRecommendedSolutions } from '../../../jsV2/components/recommendedArticles/RecommendedArticles-Actions';
import { isNewHomeExpEnabled } from '../../../jsV2/helpers/twHelper';
import { experiences } from '../../../jsV2/common/constants';
import { TOPIC_TREE } from '../../../jsV2/common/storeConstants';
import {
  isShowOnlySearchLayoutEnabled,
  isBotExperienceEnabled,
  isHomeExpTransactionLayoutEnabled,
} from '../../../experimentations/helper';

const experienceList = {
  personal: 'consumer',
  business: 'merchant',
  premier: 'consumer',
  technical: 'merchant', // TODO: update technical experience
};

const articleId = Page.getArticleIdLowerCase();
const isLoggedIn = Page.isLoggedIn();

// Compare with previous state to reduce data loading times
function isLocationOutOfSyncWithState(location, state) {
  return location.pathname !== state.location.pathname;
}

function isTopicTreeOutOfSyncWithState(location, state, isHomeV2Enabled) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  if (isHomeV2Enabled) {
    return pageInfo.experience !== state.topicList.experience || pageInfo.experience !== state[TOPIC_TREE].experience;
  }
  return pageInfo.experience !== state.topicList.experience;
}
function isPopularListOutOfSyncWithState(state) {
  return state.popular.articles && state.popular.articles.length <= 0;
}
function isTopicListOutOfSyncWithState(location, state, isHomeV2Enabled) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  if (isHomeV2Enabled) {
    return (
      pageInfo.experience !== state.location.experience ||
      pageInfo.experience !== state[TOPIC_TREE].experience ||
      pageInfo.resource.id !== state.articleList.articleId
    );
  }
  return pageInfo.experience !== state.location.experience || pageInfo.resource.id !== state.articleList.articleId;
}

function isArticleSyncOutOfWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  const stateArticleId = _get(state, 'article.article.articleId');
  return pageInfo.experience !== state.location.experience || pageInfo.resource.id !== stateArticleId;
}

function isSearchSyncOutOfWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  return (
    pageInfo.experience !== state.location.experience ||
    pageInfo.resource.searchTerm !== state.searchResult.q ||
    pageInfo.resource.channelOption !== state.searchResult.channel
  );
}

function updateRouteState(location) {
  return {
    type: 'LOCATION_CHANGE',
    payload: { location },
  };
}

function getWebAlert({ dispatch, resource }) {
  // Check if its Home Page and pass `true` to return all the alerts.
  if (resource.name === urlResource.HOME) {
    dispatch(getHelpCenterAlerts(true));
    dispatch(resetToggleRelated());
    dispatch(resetBreadcrumb());
    dispatch(resetTopicDetails());
  } else {
    dispatch(getHelpCenterAlerts(false));
  }
}

/**
 * Post chat bot eligibility call for logged out to logged in when clicked on chat bot pill.
 * @param postChatBotParams
 * @returns {Function}
 */
// eslint-disable-next-line no-unused-vars
function callPostChatBotEligibilityOnChatBotClick(postChatBotParams) {
  return (dispatch) => {
    dispatch(
      postChatBotEligibility({
        postChatBotResponse: postChatBotParams,
        showChat: displayChatBot,
        reject: displayChatBot,
      }),
    );
  };
}

/**
 * Calling postChatBot Eligibility API for checking the eligibility to show/hide chatBot
 * @returns {Function}
 */
function postChatBotEligibilityResponse(location) {
  return (dispatch) => {
    // Get current route resource name.
    const currentRouteResource = getCurrentRouteResourceName(location);
    // Post chat bot eligibility API cal for Article page and mmt check call . Don't the prompt if active convo exist
    // If current route is article
    if (currentRouteResource === urlResource.ARTICLE) {
      const customChatBotConfig = isCustomChatBotPromptConfig(articleId.toUpperCase());
      const postChatBotParams = Page.getChatBotEligibilityParams(customChatBotConfig.Tcode, 'TOPIC');
      if (isLoggedIn && customChatBotConfig.isCustomChatBotPromptEnabled) {
        dispatch(postChatBotEligibility(postChatBotParams));
        dispatch(mmtCheck());
      }
    }

    // Post chat bot eligibility API cal for Home page
    // If current route is home.
    if (currentRouteResource === urlResource.HOME) {
      if (isLoggedIn) {
        const postChatBotParams = Page.getChatBotEligibilityParams('homePage', 'GENERIC');
        dispatch(postChatBotEligibility(postChatBotParams));
      }
    }
  };
}

const returnBreadCrumb = (parentTopic, L3Topics) => {
  const items = [];
  const exp = Page.getUserExperience();
  const title = i18n.message(`smarthelp/contentToggle.content.label.desktop.${exp}`);
  const home = '/home';
  const topic = '/topic';
  if (!Page.isMobileLayout()) {
    items.push(
      {
        title,
        path: home,
        id: parentTopic.topicId,
      },
      {
        title: parentTopic.topicName,
        path: `${topic}/${parentTopic.topicId}`,
        id: parentTopic.topicId,
      },
    );
  } else {
    items.push({
      title: parentTopic.topicName,
      path: `${topic}/${parentTopic.topicId}`,
      id: L3Topics.name,
    });
  }

  return items;
};

export default function routeDidUpdate(location) {
  return (dispatch, getState) => {
    const state = getState();
    const experience = Page.getUserExperience();
    const pageInfo = { ...location, experience };

    const pageUrlInfo = getPageUrlInfoFromLocation(pageInfo);
    const { resource } = pageUrlInfo;
    const isHomeV2Enabled = isNewHomeExpEnabled();
    const newTransactionLayoutEnabled = isHomeExpTransactionLayoutEnabled();
    // We are calling this here in root level as we need to display the NAA alert in all pages.
    // This service will be triggered only If NAA alert is enabled for this market and popular questions state is out of sync with redux state.
    if (
      isLoggedIn &&
      isNaaAlertEnabledInRC() &&
      isPopularListOutOfSyncWithState(state) &&
      !isBotExperienceEnabled() &&
      !isHomeExpTransactionLayoutEnabled()
    ) {
      // V2
      if (isHomeV2Enabled && resource.name === urlResource.HOME) {
        let queryParams = {
          experience,
          pathName: resource.name,
          nna: true,
          recGuides: true,
          recAnswers: true,
          ocp: true,
        };
        if (experience === experiences.TECHNICAL) {
          queryParams = {
            experience,
            channel: 'mts',
            pathName: resource.name,
            nna: false,
            recGuides: false,
            recAnswers: true,
            ocp: true,
          };
        }
        dispatch(getRecommendedSolutions(queryParams));
      } else {
        dispatch(getPopularQuestions(experience, resource.name));
      }
    } else if (
      resource.name === urlResource.HOME &&
      isPopularListOutOfSyncWithState(state) &&
      !isBotExperienceEnabled() &&
      !isHomeExpTransactionLayoutEnabled()
    ) {
      // Irrespective we need to fire in HOME page
      // V2
      if (isHomeV2Enabled && resource.name === urlResource.HOME) {
        let queryParams = {
          experience,
          pathName: resource.name,
          nna: true,
          recGuides: true,
          recAnswers: true,
          ocp: true,
        };
        if (experience === experiences.TECHNICAL) {
          queryParams = {
            experience,
            channel: 'mts',
            pathName: resource.name,
            nna: false,
            recGuides: false,
            recAnswers: true,
            ocp: true,
          };
        }
        dispatch(getRecommendedSolutions(queryParams));
      } else {
        dispatch(getPopularQuestions(experience, resource.name));
      }
    }

    if (isLocationOutOfSyncWithState(pageInfo, state)) {
      dispatch(updateRouteState(pageInfo));
      // need to remove this for new hc design to avoid duplicate web alerts api call
      getWebAlert({ dispatch, resource });
      // Do not fire post chatbot eligibility api call on icf pages
      if (resource.name !== urlResource.PAYPALGUIDE) {
        dispatch(postChatBotEligibilityResponse(location));
      }

      const pathName = pageInfo && pageInfo.pathname;
      const icfId = Page.getIcfId(pathName);

      if (resource.name === urlResource.PAYPALGUIDE && (!icfId || icfId.indexOf(urlResource.ICF) === -1)) {
        // checking for icf id in the url and index of the icfId
        // ICF Topic page
        dispatch(getICFGuides());
      }

      if (resource.name === urlResource.PAYPALGUIDE && icfId && icfId.indexOf(urlResource.ICF) !== -1) {
        // ICF Articles page
        dispatch(fetchGuideContents(`icf_id=${icfId}`));
        dispatch(icfClicked(icfId));
      }
    }
    const L3TopicClicked = state.L3TopicList.isL3TopicClicked;

    if (resource.name === urlResource.HOME && isPopularListOutOfSyncWithState(state)) {
      dispatch(resetToggleRelated());
      dispatch(resetTopicDetails());
    }
    if (resource.name === urlResource.HOME && isTopicTreeOutOfSyncWithState(pageInfo, state, isHomeV2Enabled)) {
      // TODO:: Temporary solution to call new topic tree action.
      //  This needs to be refactored to call new function once we are in a good shape with V2 version.
      if (isHomeV2Enabled || newTransactionLayoutEnabled) {
        dispatch(fetchTopicTreeV2(experience));
      } else {
        dispatch(fetchTopicTree(experience));
      }
      dispatch(resetToggleRelated());
    }
    if (resource.name === urlResource.HOME && L3TopicClicked) {
      dispatch(fetchTopicTree(experience));
    }

    const isL3TopicPage = getL3TopicInfo(pageInfo);
    if (
      resource.name === urlResource.TOPIC &&
      isTopicListOutOfSyncWithState(pageInfo, state, isHomeV2Enabled) &&
      !isL3TopicPage
    ) {
      dispatch(getArticleListByTopic(resource.id, experience, L3TopicClicked, isL3TopicPage));
      dispatch(resetToggleRelated());
    }

    const topicList = state.topicList && state.topicList.topics;
    const topicLists = state.L3TopicList || {};
    const SelectedL3Article = topicLists.SelectedL3Article;
    const subTopic = getL3TopicInfo(pageInfo);
    // For L3 Topic Topics
    if (
      resource.name === urlResource.TOPIC &&
      ((!L3TopicClicked && subTopic) || (Array.isArray(topicList) && !topicList.length) || SelectedL3Article)
    ) {
      dispatch(isL3TopicClicked());
      let L2Topics = topicLists.L2Topics;
      L2Topics = L2Topics.map(({ subTopicId, subTopicName, articles }) => ({
        topicId: subTopicId,
        topicName: subTopicName,
        faqList: articles,
      }));

      const L3Topics = topicLists.SelectedL2TopicDetails;
      const blacklistedMarketICFFlag = topicLists.blacklistedMarketICFFlag;
      const parentTopic = topicLists.SelectedL1TopicDetails;

      if (Array.isArray(L3Topics) && L3Topics.length && Array.isArray(L2Topics) && L2Topics.length) {
        dispatch(updateBreadcrumb(returnBreadCrumb(parentTopic, L3Topics)));
        dispatch(receiveL3ArticlesResponse(L3Topics)); // send level 3 topics
        dispatch(updateL2TopicTree({ L2Topics, experience, blacklistedMarketICFFlag }));
      } else {
        // if the customer directly lands on the izettle page
        let isL2TopicSelected = false;
        if (subTopic) {
          // on Level 2 direct refresh, topiclist is empty so adding this condition
          isL2TopicSelected = true;
        }

        dispatch(fetchTopicTree(experience, resource.id, isL2TopicSelected));
      }
    }

    // To update the breadcrumbs on location change.
    if (resource.name === urlResource.ARTICLE && state.article && state.article.article !== undefined) {
      dispatch(updateBreadCrumbFromArticleContent(state.article.article, state.L3TopicList.breadCrumb));
    }

    if (resource.name === urlResource.ARTICLE && isArticleSyncOutOfWithState(pageInfo, state)) {
      dispatch(fetchArticleContent(experience, resource.id));

      // Request Machine Translation localized article content
      if (Page.isMachineTranslation()) {
        dispatch(fetchMachineArticle({ faqId: resource.id, locale: Page.getMachineTranslationMarket() }));
      }
    }

    if (resource.name === urlResource.SEARCH && isSearchSyncOutOfWithState(pageInfo, state)) {
      const q = resource.searchTerm;
      let channel = Page.getChannel(resource.channelOption);

      // Update SWS channel to fire API
      if (state.searchFilters && !state.searchFilters.formUpdated && !q) {
        channel = 'faq';
        // passing MTS channel, if the user not make selection, has technical experience and no channel in the url
      } else if (
        state.searchFilters &&
        !state.searchFilters.formUpdated &&
        Page.isTechnicalExp() &&
        !resource.channelOption
      ) {
        channel = 'mts';
      }

      dispatch(clearSuggestions());
      dispatch(
        fetchQueryParams({
          q,
          channel,
        }),
      );

      if (resource.searchTerm) {
        // Innovation Guild: Search only Experiment in Help Center
        if (isBotExperienceEnabled() || isShowOnlySearchLayoutEnabled()) {
          dispatch(
            fetchArticleSearchOnlyFaqBot(resource.searchTerm, experienceList[experience || 'personal'], channel),
          );
        } else {
          dispatch(fetchArticleSearchResult(resource.searchTerm, experienceList[experience || 'personal'], channel));
        }
      }
    }
  };
}
